import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * EmptyState — primitive "rien a afficher"
 *
 * Quand utiliser :
 *   - Table ou liste vide apres chargement
 *   - Resultats de recherche a 0
 *   - Premiere visite (onboarding)
 *   - Section metier non encore configuree
 *
 * Slots :
 *   - icon        : ReactNode optionnel (lucide, emoji, SVG)
 *   - title       : string ou ReactNode — titre court
 *   - description : string ou ReactNode — explication 1-2 lignes max
 *   - children    : ReactNode — CTA(s), generalement un <Button>
 *
 * Variantes :
 *   - size   : 'sm' (inline 16px icon) | 'md' (defaut, 24px) | 'lg' (40px, usage full-page)
 *   - tone   : 'subtle' (defaut, pas de bordure) | 'framed' (bordure + bg surface-low)
 *
 * Usage :
 *   <EmptyState
 *     icon={<FileIcon />}
 *     title="Aucun document"
 *     description="Ajoute ton premier document pour commencer."
 *   >
 *     <Button>Ajouter un document</Button>
 *   </EmptyState>
 */
const emptyStateVariants = cva(
  'flex flex-col items-center justify-center text-center',
  {
    variants: {
      size: {
        sm: 'py-6 gap-2',
        md: 'py-10 gap-3',
        lg: 'py-16 gap-4',
      },
      tone: {
        subtle: '',
        framed: 'rounded-xl border border-ink-ghost/30 bg-surface-low px-6',
      },
    },
    defaultVariants: {
      size: 'md',
      tone: 'subtle',
    },
  }
)

const iconWrapVariants = cva(
  'flex items-center justify-center rounded-xl bg-violet-50 text-violet-500',
  {
    variants: {
      size: {
        sm: 'w-8 h-8 [&>svg]:w-4 [&>svg]:h-4',
        md: 'w-12 h-12 [&>svg]:w-5 [&>svg]:h-5',
        lg: 'w-16 h-16 [&>svg]:w-7 [&>svg]:h-7',
      },
    },
    defaultVariants: { size: 'md' },
  }
)

const titleVariants = cva('text-ink font-semibold', {
  variants: {
    size: {
      sm: 'text-body',
      md: 'text-h3',
      lg: 'text-h2',
    },
  },
  defaultVariants: { size: 'md' },
})

const descVariants = cva('text-ink-muted max-w-md', {
  variants: {
    size: {
      sm: 'text-caption',
      md: 'text-body',
      lg: 'text-body',
    },
  },
  defaultVariants: { size: 'md' },
})

export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof emptyStateVariants> {
  icon?: React.ReactNode
  title: React.ReactNode
  description?: React.ReactNode
}

const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ className, size, tone, icon, title, description, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(emptyStateVariants({ size, tone }), className)}
        {...props}
      >
        {icon ? <div className={iconWrapVariants({ size })}>{icon}</div> : null}

        {typeof title === 'string' ? (
          <h3 className={titleVariants({ size })}>{title}</h3>
        ) : (
          title
        )}

        {description ? (
          typeof description === 'string' ? (
            <p className={descVariants({ size })}>{description}</p>
          ) : (
            description
          )
        ) : null}

        {children ? <div className="mt-2 flex items-center gap-2">{children}</div> : null}
      </div>
    )
  }
)
EmptyState.displayName = 'EmptyState'

export { EmptyState, emptyStateVariants }
